import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { CampaignsContext } from "contexts/Campaigns";
import { useContext, useState } from "react";
import { Campaign } from "schemas/dashboard";

interface IProps {
  campaign: Campaign;
  typographySx?: object;
}

export default function EditableCampaignName({
  campaign,
  typographySx,
}: IProps) {
  const { renameCampaign } = useContext(CampaignsContext);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(campaign.name);
  const [saving, setSaving] = useState(false);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValue(campaign.name);
    setEditing(true);
  };

  const save = async () => {
    const name = value.trim();
    if (!name || name === campaign.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await renameCampaign(campaign.id!, name);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        value={value}
        autoFocus
        fullWidth
        disabled={saving}
        variant="standard"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            setEditing(false);
          }
        }}
        InputProps={{ sx: typographySx }}
        sx={{
          "& .MuiInput-underline:before": {
            borderBottomColor: grey[300],
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottomColor: grey[400],
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: grey[400],
          },
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Box sx={{ minWidth: 0, ...typographySx }}>{campaign.name}</Box>
      <Tooltip title="Rename campaign" placement="top">
        <IconButton
          size="small"
          onClick={startEditing}
          sx={{
            flexShrink: 0,
            color: grey[400],
            "&:hover": { color: grey[700] },
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-pen"
            sx={{ fontSize: 11 }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
